import { useMemo, type VoidFunctionComponent } from 'react';

import { IssueScopeKind, type LicensingIssueType, type Scope, type Status } from '../../../../../shared/issuesV2';
import { Icon, type IconClassName, icons } from '../../../generic';
import Flex from '../../../generic/Flex';
import ListRowDetailsSeparator from '../../../generic/List/ListRowDetailsSeparator';

import styles from './IssueDetailsProjectsTable.module.less';

const ISSUE_TYPE_ICON_MAP: Record<LicensingIssueType, IconClassName> = {
  policy_conflict: icons.banOutline,
  policy_flag: icons.flagOutline,
  unlicensed_dependency: icons.questionCircleO,
  unconcluded_dependency: icons.questionCircleO,
};

const ISSUE_TYPE_COLOR_MAP: Record<LicensingIssueType, string> = {
  policy_conflict: styles.issueTypeIconRed,
  policy_flag: styles.issueTypeIconYellow,
  unlicensed_dependency: styles.issueTypeIconGray,
  unconcluded_dependency: styles.issueTypeIconGray,
};

type Props = {
  status: Status;
  issueType: LicensingIssueType;
  issueLicense: string;
  concludedLicenses: string[] | undefined;
  scope: Scope;
};

export const LicenseIssueDetailContent: VoidFunctionComponent<Props> = ({
  status,
  issueType,
  issueLicense,
  concludedLicenses,
  scope,
}) => {
  const issueTypeLabelMap: Record<LicensingIssueType, string> = useMemo(
    () => ({
      policy_flag: issueLicense,
      policy_conflict: issueLicense,
      unlicensed_dependency: 'Unlicensed',
      unconcluded_dependency: 'Unconcluded',
    }),
    [issueLicense],
  );

  const [icon, iconClass] = useMemo(() => {
    const iconName = ISSUE_TYPE_ICON_MAP[issueType] ?? icons.questionCircleO;
    const iconClassName = status === 'active' ? ISSUE_TYPE_COLOR_MAP[issueType] : styles.issueTypeIconGray;
    return [iconName, iconClassName];
  }, [issueType, status]);

  return (
    <div className={styles.licenseIssueDetail}>
      <div className={iconClass}>
        <Icon icon={icon} />
      </div>
      <div>
        {issueTypeLabelMap[issueType]} ({status})
      </div>
      {scope.type !== IssueScopeKind.ReleaseGroup && (
      <Flex align="center" justify="space-between" gap="4px">
        <ListRowDetailsSeparator />
        {concludedLicenses ? `Concluded license: ${concludedLicenses.join(' AND ')}` : 'No concluded license'}
      </Flex>
      )}
    </div>
  );
};
